import React, { useState, useEffect } from 'lib/react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { makeStyles } from 'lib/components';
import {
  useAxisTooltip, useDrawingArea, useXScale, useSvgRef,
} from '@mui/x-charts';
import { getMarkStyle } from '../Consumption.utils';

// #genai Extract the same trailing numeric series index without a backtracking regular expression.
const getTrailingSeriesIndex = (seriesId) => {
  const value = String(seriesId ?? '');
  let start = value.length;
  while (start > 0 && value[start - 1] >= '0' && value[start - 1] <= '9') {
    start -= 1;
  }
  return Number(value.slice(start) || 0);
};

const useStyles = makeStyles((theme) => ({
  tooltipContainer: {
    pointerEvents: 'none',
    position: 'fixed',
  },
  tooltip: {
    backgroundColor: theme.evPalette.salt[100],
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    padding: '12px 16px',
    pointerEvents: 'none',
    width: '164px',
    position: 'relative',
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))',
  },
  arrowLeft: {
    left: '-8px',
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderRight: `8px solid ${theme.evPalette.salt[100]}`,
  },
  arrowRight: {
    right: '-8px',
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: `8px solid ${theme.evPalette.salt[100]}`,
  },
  header: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.evPalette.charcoal[900],
    marginBottom: '12px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mark: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  label: {
    fontSize: '8px',
    fontWeight: 600,
    color: theme.evPalette.charcoal[800],
    textTransform: 'uppercase',
    flex: 1,
  },
  value: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.evPalette.charcoal[900],
    marginLeft: '16px',
  },
}));

function HistoricUsageTooltip({ series }) {
  const classes = useStyles();
  const tooltipData = useAxisTooltip();
  const { left, top, height } = useDrawingArea();
  const xScale = useXScale();
  const svgRef = useSvgRef();
  const [mouseY, setMouseY] = useState(null);
  const [svgRect, setSvgRect] = useState(null);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return undefined;

    const updateRect = () => setSvgRect(svgElement.getBoundingClientRect());
    const handleMouseMove = (e) => {
      updateRect();
      setMouseY(e.clientY - svgElement.getBoundingClientRect().top);
    };
    const handleMouseLeave = () => setMouseY(null);

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    svgElement.addEventListener('mousemove', handleMouseMove);
    svgElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      svgElement.removeEventListener('mousemove', handleMouseMove);
      svgElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [svgRef]);

  if (!tooltipData?.axisValue || !svgRect) return null;

  const { axisValue, axisFormattedValue, seriesItems } = tooltipData;

  const findSeriesConfig = (item) => series.find((s) => s.dataKey === item.dataKey)
    || series[getTrailingSeriesIndex(item.seriesId)];

  const visibleItems = seriesItems?.filter((item) => {
    const config = findSeriesConfig(item);
    // Hide "Not Started" bars from tooltip
    return item.value > 0 && config?.dataKey !== 'notStartedBar';
  }) || [];
  if (visibleItems.length === 0) return null;

  const barX = xScale(axisValue);
  const bandwidth = xScale.bandwidth?.() || 50;
  const barRightX = svgRect.left + left + barX + bandwidth;
  const barLeftX = svgRect.left + left + barX;
  const TOOLTIP_GAP = 12;
  const TOOLTIP_WIDTH = 164;
  const VIEWPORT_PADDING = 10;
  const spaceOnRight = window.innerWidth - barRightX - TOOLTIP_GAP;
  const spaceOnLeft = barLeftX - VIEWPORT_PADDING;
  const positionOnRight = spaceOnRight >= TOOLTIP_WIDTH + TOOLTIP_GAP || spaceOnRight > spaceOnLeft;

  const tooltipX = positionOnRight
    ? barRightX + TOOLTIP_GAP
    : Math.max(VIEWPORT_PADDING, barLeftX - TOOLTIP_GAP - TOOLTIP_WIDTH);
  const tooltipY = svgRect.top + (mouseY != null
    ? Math.max(top + 50, Math.min(mouseY, top + height - 50))
    : top + (height / 2));

  return ReactDOM.createPortal(
    <div
      className={classes.tooltipContainer}
      style={{
        position: 'fixed',
        left: `${tooltipX}px`,
        top: `${tooltipY}px`,
        transform: 'translateY(-50%)',
        zIndex: 9999,
      }}
    >
      <div className={`${classes.arrow} ${positionOnRight ? classes.arrowLeft : classes.arrowRight}`} />
      <div className={classes.tooltip}>
        <div className={classes.header}>
          {axisFormattedValue}
        </div>
        {visibleItems.map((item, index) => {
          const config = findSeriesConfig(item);
          const markColor = config?.color || item.color || '#3E649B';

          return (
            <div key={item.seriesId || index} className={classes.row}>
              <div className={classes.mark} style={getMarkStyle(markColor, series)} />
              <span className={classes.label}>{config?.label || item.label || 'Value'}</span>
              <span className={classes.value}>{item.formattedValue}</span>
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

HistoricUsageTooltip.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string,
    color: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
};

export default HistoricUsageTooltip;
